import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronDownIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { educationLevels } from '../data/degrees';
import { sanitizeTcpaHtml } from '../utils/sanitizeHtml';
import { TCPADisclosure } from './TCPADisclosure';
import { getProgramMatch, type ProgramTemplate } from '../data/programTemplates';
import { clicksSearch, leadsSearch, tcpaPost, tcpaSearch, type ContactProfile, type MMSchool, type SearchProfile, type TCPAResult } from '../services/mediaMatchers';
const startDateOptions = ['Immediately', '1–3 months', '3–6 months', '6–12 months', 'More than a year', 'Undecided'];
function getGradYearOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let y = currentYear + 2; y >= currentYear - 60; y--) years.push(String(y));
  years.push('Before 1965');
  return years;
}
const gradYearOptions = getGradYearOptions();
const STEP_LABELS = ['Program', 'Background', 'Location', 'Contact'];

/**
 * Minimum a picker option needs. `ProgramTemplate` satisfies this, and so do
 * the generated degree × program combinations, which have no template entry.
 */
export interface MatchFormProgram {
  slug: string;
  name: string;
  credential: string;
}
interface HeroMatchFormProps {
  /** Programs offered in the Step 0 picker. */
  programs: MatchFormProgram[];
  /** Pre-selected program, when the page is about one. */
  initialProgramSlug?: string;
  /** Show each option's domain, for the all-programs index. */
  domainNameFor?: (program: MatchFormProgram) => string | undefined;
  /**
   * Media Matchers codes for an option. Defaults to the program-template
   * lookup; generated combinations pass their own codes instead.
   */
  matchFor?: (program: MatchFormProgram) => {
    degreeInterest?: string;
    areaStudy?: string;
    mProgram?: string;
  };
  heading?: string;
  subtext?: string;
  /**
   * Fires whenever the visitor moves between steps, so the hosting page can
   * give the form more room once it reaches the contact step.
   */
  onStepChange?: (step: number) => void;
  /** Lays the contact fields out in two columns when the card is wide. */
  wide?: boolean;
}

/**
 * Lead form that lives directly in a program page hero, so a visitor can
 * complete the whole match without leaving the page they landed on.
 *
 * Step 0 picks the program, 1 collects background, 2 location, 3 contact. When
 * the page is about a single program, `initialProgramSlug` answers step 0 and
 * the picker is skipped entirely. Both Media Matchers Searches fire when step 2
 * completes — before the contact step renders — so the partner's own disclosure
 * is the text shown beside the consent checkbox.
 */
export function HeroMatchForm({
  programs,
  initialProgramSlug,
  domainNameFor,
  matchFor,
  heading = 'Get Matched Now',
  subtext = 'Accredited programs. No obligation.',
  onStepChange,
  wide = false
}: HeroMatchFormProps) {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  // A page about one program has already answered the program question, so the
  // picker is skipped rather than asking the visitor to restate it.
  const hasFixedProgram = !!initialProgramSlug;
  const firstStep = hasFixedProgram ? 1 : 0;
  const [step, setStep] = useState(firstStep);
  const [programSlug, setProgramSlug] = useState(initialProgramSlug || '');
  const [programError, setProgramError] = useState('');
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();

  // Populates the LeadiD and TrustedForm hidden inputs before anything fires.
  useLeadTracking();
  const [leads, setLeads] = useState<MMSchool[] | null>(null);
  const [tcpa, setTcpa] = useState<TCPAResult | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [tcpaFailed, setTcpaFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const searchFired = useRef(false);
  useEffect(() => {
    onStepChange?.(step);
  }, [step, onStepChange]);
  const selectedProgram = programs.find(p => p.slug === programSlug);
  const buildProfile = useCallback((): SearchProfile => {
    const resolveMatch = matchFor || ((p: MatchFormProgram) => getProgramMatch(p as ProgramTemplate));
    const context = selectedProgram ? resolveMatch(selectedProgram) : {};
    return {
      degreeInterest: context.degreeInterest || '',
      areaStudy: context.areaStudy || '',
      mProgram: context.mProgram || '',
      currentEducation: formData.currentEducation,
      gradYear: formData.gradYear,
      startTimeframe: formData.startDate,
      militaryAffiliation: formData.isMilitary,
      zipCode: formData.zipCode.replace(/\D/g, ''),
      address: formData.address,
      city: formData.city,
      state: formData.state
    };
  }, [selectedProgram, formData, matchFor]);

  /**
   * TCPA Search runs as soon as the ZIP is in, so the partner's own consent
   * language is on screen before the visitor ticks the box.
   */
  const runTcpaSearch = useCallback(async () => {
    setSearchLoading(true);
    setTcpaFailed(false);
    const result = await tcpaSearch(buildProfile());
    setTcpa(result);
    if (!result) setTcpaFailed(true);
    setSearchLoading(false);
    return result;
  }, [buildProfile]);
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
        }
      }
    } catch {
      /* Fall through to the manual city/state fields. */
    } finally {
      setZipLookedUp(true);
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleProgramNext = (event: React.FormEvent) => {
    event.preventDefault();
    if (!programSlug) {
      setProgramError('Please choose a program to continue.');
      return;
    }
    setStep(1);
  };
  const handleBackgroundNext = (event: React.FormEvent) => {
    event.preventDefault();
    if (validateUniStep1()) setStep(2);
  };
  const handleLocationNext = (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateUniStep2()) return;
    // Last field the TCPA payload needs, so its disclosure is in hand by the
    // time the consent checkbox renders.
    if (!searchFired.current) {
      searchFired.current = true;
      runTcpaSearch();
    }
    setStep(3);
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError('');
    if (!validateUniStep3()) return;
    if (!tcpa) {
      setTcpaFailed(true);
      setSubmitError('We could not load the consent details. Please retry before submitting.');
      return;
    }
    setIsSubmitting(true);
    const profile = buildProfile();
    const contact: ContactProfile = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: stripPhone(formData.phone)
    };
    await tcpaPost(tcpa.mRecordID, contact);
    // Leads Search runs here so it carries the complete profile, and excludes
    // the brand the TCPA call already matched.
    const [matchedLeads, clicks] = await Promise.all([leadsSearch(profile, contact, tcpa.mBrandID), clicksSearch(profile, contact, tcpa.mBrandID)]);
    setLeads(matchedLeads || []);
    setIsSubmitting(false);
    navigate('/matches', {
      state: {
        leads: matchedLeads || [],
        clicks: clicks || [],
        profile,
        contact,
        disclosure: tcpa.disclosure,
        excludeBrand: tcpa.mBrandID
      }
    });
  };
  const totalSteps = STEP_LABELS.length - firstStep;
  const currentStepNumber = step - firstStep + 1;
  const progressPercent = Math.round(currentStepNumber / totalSteps * 100);
  const selectClass = (hasError: boolean) => `block w-full pl-4 pr-10 py-3 text-sm rounded-lg border bg-white text-gray-900 appearance-none cursor-pointer transition-colors focus:outline-none focus:ring-2 ${hasError ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:border-navy focus:ring-navy/20'}`;
  const inputClass = (hasError: boolean) => `block w-full px-4 py-3 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 ${hasError ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:border-navy focus:ring-navy/20'}`;
  const labelClass = 'block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider';
  const primaryButton = 'w-full bg-cta hover:bg-cta-dark disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3.5 px-6 rounded-lg text-sm tracking-wide uppercase transition-colors duration-200 inline-flex items-center justify-center gap-2';
  const backButton = 'inline-flex items-center justify-center px-4 py-3.5 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60 transition-colors duration-200';
  const domains = domainNameFor ? Array.from(new Set(programs.map(p => domainNameFor(p) || '').filter(Boolean))) : [];
  const programOptions = domainNameFor ? domains.map(domainName => <optgroup key={domainName} label={domainName} data-id="element-342" data-mp="1jdsdc6ccf8ur">
          {programs.filter(p => domainNameFor(p) === domainName).map(p => <option key={p.slug} value={p.slug} data-id="element-343" data-mp="1elhgjccwg36f">
                {p.name} ({p.credential})
              </option>)}
        </optgroup>) : programs.map(p => <option key={p.slug} value={p.slug} data-id="element-344" data-mp="14qpffyk3yy6w">
          {p.name} ({p.credential})
        </option>);
  const renderStep = () => {
    if (step === 0) {
      return <form onSubmit={handleProgramNext} className="space-y-5" data-id="element-345" data-mp="vpr8p8iacckm">
          <div data-id="element-346" data-mp="qpwk5blzfz2q">
            <label htmlFor="hero-program" className={labelClass} data-id="element-347" data-mp="qqeokvistjlq">
              Program of Interest
            </label>
            <div className="relative" data-id="element-348" data-mp="2q1ftvsf41bdg">
              <select id="hero-program" value={programSlug} onChange={e => {
              setProgramSlug(e.target.value);
              if (e.target.value) setProgramError('');
            }} aria-invalid={!!programError} className={selectClass(!!programError)} data-id="element-349" data-mp="2q0efbdyvy6qz">
                <option value="" data-id="element-350" data-mp="32di3pxc1xw8s">-- Choose a Program --</option>
                {programOptions}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400" data-id="element-351" data-mp="37e5z0v7bcz8t">
                <ChevronDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-352" data-mp="2ene5k5hmuop6" />
              </span>
            </div>
            {programError && <p className="mt-1.5 text-xs text-red-600" data-id="element-353" data-mp="2jlpe8cw6yt5t">{programError}</p>}
          </div>
          <button type="submit" className={primaryButton} data-id="element-354" data-mp="2novft78czw5y">
            Get Started
          </button>
        </form>;
    }
    if (step === 1) {
      return <form onSubmit={handleBackgroundNext} className="space-y-4" data-id="element-355" data-mp="2xbqyaxxa2ny">
          <div data-id="element-356" data-mp="2zpmsm0ffiwk">
            <label htmlFor="hero-education" className={labelClass} data-id="element-357" data-mp="1zy8zm51m1jcy">
              Highest Level of Education
            </label>
            <div className="relative" data-id="element-358" data-mp="1uycuu3l39nwc">
              <select id="hero-education" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClass(!!errors.currentEducation)} data-id="element-359" data-mp="1v1ihzda5qfpf">
                <option value="" data-id="element-360" data-mp="1g85nok0j7ray">-- Please Select --</option>
                {educationLevels.map(level => <option key={level} value={level} data-id="element-361" data-mp="1q01k7qmyz9d2">
                    {level}
                  </option>)}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400" data-id="element-362" data-mp="1l81pqdpo58nv">
                <ChevronDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-363" data-mp="xhxm46chd8wv" />
              </span>
            </div>
            {errors.currentEducation && <p className="mt-1.5 text-xs text-red-600" data-id="element-364" data-mp="3480vyzgpkmtv">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-365" data-mp="3913w5qfzquj1">
            <label htmlFor="hero-gradyear" className={labelClass} data-id="element-366" data-mp="2v7kxy5iqi7we">
              High School Graduation Year
            </label>
            <div className="relative" data-id="element-367" data-mp="305w7sg914iem">
              <select id="hero-gradyear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClass(!!errors.gradYear)} data-id="element-368" data-mp="306xk0o1s7axx">
                <option value="" data-id="element-369" data-mp="3dylukmwdtrcs">-- Please Select --</option>
                {gradYearOptions.map(year => <option key={year} value={year} data-id="element-370" data-mp="3iwdua339qhrj">
                    {year}
                  </option>)}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400" data-id="element-371" data-mp="22l08jaltk5vh">
                <ChevronDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-372" data-mp="2mrnav88p5r8l" />
              </span>
            </div>
            {errors.gradYear && <p className="mt-1.5 text-xs text-red-600" data-id="element-373" data-mp="2i058qguxtkm1">{errors.gradYear}</p>}
          </div>

          <div data-id="element-374" data-mp="2dzv355b24w7p">
            <label htmlFor="hero-start" className={labelClass} data-id="element-375" data-mp="2dzlepm5nux44">
              When Would You Like to Start?
            </label>
            <div className="relative" data-id="element-376" data-mp="35qj0055p7ffk">
              <select id="hero-start" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClass(!!errors.startDate)} data-id="element-377" data-mp="35phnrxcy4mw9">
                <option value="" data-id="element-378" data-mp="2rqqgatsl9zh3">-- Please Select --</option>
                {startDateOptions.map(opt => <option key={opt} value={opt} data-id="element-379" data-mp="1laefsm2ao5ic">
                    {opt}
                  </option>)}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400" data-id="element-380" data-mp="1gatgubn4jx5m">
                <ChevronDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-381" data-mp="169hbexdpe80x" />
              </span>
            </div>
            {errors.startDate && <p className="mt-1.5 text-xs text-red-600" data-id="element-382" data-mp="1g5ak25oaxhdf">{errors.startDate}</p>}
          </div>

          <div className="flex gap-3 pt-1" data-id="element-383" data-mp="1kwsl0tqbrhye">
            {!hasFixedProgram && <button type="button" onClick={() => setStep(0)} className={backButton} data-id="element-384" data-mp="1oye4ixxd6uhk">
                <ChevronLeftIcon className="w-4 h-4 mr-1" aria-hidden="true" data-id="element-385" data-mp="1tvnvii0eje7i" />
                Back
              </button>}
            <button type="submit" className={primaryButton} data-id="element-386" data-mp="23vemi9pr7b2u">
              Continue
            </button>
          </div>
        </form>;
    }
    if (step === 2) {
      return <form onSubmit={handleLocationNext} className="space-y-4" data-id="element-387" data-mp="4knb3bhlyam3">
          <div data-id="element-388" data-mp="9hx9hgmz5wm2">
            <label htmlFor="hero-zip" className={labelClass} data-id="element-389" data-mp="9hnflhyt75b3">
              ZIP Code
            </label>
            <input id="hero-zip" type="text" inputMode="numeric" maxLength={5} value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} placeholder="12345" className={inputClass(!!errors.zipCode)} data-id="element-390" data-mp="1isaw76ztdbw" />
            {errors.zipCode && <p className="mt-1.5 text-xs text-red-600" data-id="element-391" data-mp="u4ttohjpkde3">{errors.zipCode}</p>}
            {zipLoading && <p className="mt-2 flex items-center text-xs text-gray-500" data-id="element-392" data-mp="p8d5jncidz10">
                <LoaderIcon className="w-3 h-3 mr-1.5 animate-spin" aria-hidden="true" data-id="element-393" data-mp="136un13btmfah" />
                Looking up your location...
              </p>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-394" data-mp="1ms37p3cue086">
                <div data-id="element-395" data-mp="1mph2vbhc5ytn">
                  <label htmlFor="hero-city" className={labelClass} data-id="element-396" data-mp="18sbydfx9t80i">
                    City
                  </label>
                  <input id="hero-city" type="text" value={formData.city} onChange={e => updateFormData('city', e.target.value)} placeholder="City" className={inputClass(false)} data-id="element-397" data-mp="1dq3smgle167v" />
                </div>
                <div data-id="element-398" data-mp="pzhkyqfapnye">
                  <label htmlFor="hero-state" className={labelClass} data-id="element-399" data-mp="u35uvfregvmt">
                    State
                  </label>
                  <input id="hero-state" type="text" value={formData.state} onChange={e => updateFormData('state', e.target.value)} placeholder="State" className={inputClass(false)} data-id="element-400" data-mp="g2v40cq3bh1n" />
                </div>
              </div>
              <div data-id="element-401" data-mp="vjlau6yss56f">
                <label htmlFor="hero-address" className={labelClass} data-id="element-402" data-mp="rjkkbnyfc3j0">
                  Street Address{' '}
                  <span className="font-normal normal-case text-gray-400" data-id="element-403" data-mp="ml9ahd84pt0s">
                    (optional)
                  </span>
                </label>
                <input id="hero-address" type="text" value={formData.address} onChange={e => updateFormData('address', e.target.value)} placeholder="123 Main St" className={inputClass(false)} data-id="element-404" data-mp="hnpw8dbflg8u" />
              </div>
            </>}

          <div className="flex gap-3 pt-1" data-id="element-405" data-mp="3uzhw21c8hjjg">
            <button type="button" onClick={() => setStep(1)} className={backButton} data-id="element-406" data-mp="3v2dv5sztie9h">
              <ChevronLeftIcon className="w-4 h-4 mr-1" aria-hidden="true" data-id="element-407" data-mp="25k74vsabwgo0" />
              Back
            </button>
            <button type="submit" className={primaryButton} data-id="element-408" data-mp="2ah7ci7mttrh8">
              Continue
            </button>
          </div>
        </form>;
    }
    return <form onSubmit={handleSubmit} className="space-y-4" data-id="element-409" data-mp="2jix53gsg2wgl">
        <input id="leadid_token" name="leadid_token" type="hidden" data-id="element-410" data-mp="2jidvqogv1ge6" />
        <input id="xxTrustedFormCertUrl" name="xxTrustedFormCertUrl" type="hidden" data-id="element-411" data-mp="1mtz6tolhkgeo" />

        <div className={wide ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4' : 'grid grid-cols-2 gap-3'} data-id="element-412" data-mp="1rsiym5mrgmey">
          <div data-id="element-413" data-mp="20l4cb7bjc2wx">
            <label htmlFor="hero-first" className={labelClass} data-id="element-414" data-mp="20lm7e0mgob39">
              First Name
            </label>
            <input id="hero-first" type="text" autoComplete="given-name" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} placeholder="Jane" className={inputClass(!!errors.firstName)} data-id="element-415" data-mp="140c3tawoonhn" />
            {errors.firstName && <p className="mt-1.5 text-xs text-red-600" data-id="element-416" data-mp="okjrz9z6o2bw">{errors.firstName}</p>}
          </div>
          <div data-id="element-417" data-mp="thtiyu280m1u">
            <label htmlFor="hero-last" className={labelClass} data-id="element-418" data-mp="tg8wlrq289f2">
              Last Name
            </label>
            <input id="hero-last" type="text" autoComplete="family-name" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} placeholder="Doe" className={inputClass(!!errors.lastName)} data-id="element-419" data-mp="3svnek5if29ao" />
            {errors.lastName && <p className="mt-1.5 text-xs text-red-600" data-id="element-420" data-mp="398y4zsq04qg4">{errors.lastName}</p>}
          </div>

        <div className={wide ? undefined : 'col-span-2'} data-id="element-421" data-mp="3e6zky4s4s9nd">
          <label htmlFor="hero-email" className={labelClass} data-id="element-422" data-mp="36pkxnt6mu93z">
            Email Address
          </label>
          <input id="hero-email" type="email" autoComplete="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} placeholder="jane@example.com" className={inputClass(!!errors.email)} data-id="element-423" data-mp="31pox63x58zt6" />
          {errors.email && <p className="mt-1.5 text-xs text-red-600" data-id="element-424" data-mp="2nsa1wik7lxta">{errors.email}</p>}
        </div>

        <div className={wide ? undefined : 'col-span-2'} data-id="element-425" data-mp="2ixdxeixsdhza">
          <label htmlFor="hero-phone" className={labelClass} data-id="element-426" data-mp="2e1ff9d31ww7b">
            Phone Number
          </label>
          <input id="hero-phone" type="tel" autoComplete="tel" maxLength={13} value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} placeholder="(555)123-4567" className={inputClass(!!errors.phone)} data-id="element-427" data-mp="292c7vlzlmdw6" />
          {errors.phone && <p className="mt-1.5 text-xs text-red-600" data-id="element-428" data-mp="1ufdkc23v43mq">{errors.phone}</p>}
          </div>
        </div>

        <div className={wide ? 'max-w-3xl' : undefined} data-id="element-429" data-mp="1ggdy113to0tq">
        {searchLoading ? <p className="flex items-center gap-2 text-xs text-gray-500 pt-1" data-id="element-430" data-mp="1lep6b7egy39u">
            <LoaderIcon className="w-3.5 h-3.5 animate-spin" aria-hidden="true" data-id="element-431" data-mp="2938bvf64w1y8" />
            Loading consent details...
          </p> : tcpaFailed ? <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-id="element-432" data-mp="2e4e65hffzx9v">
            <p className="text-xs font-medium text-red-800" data-id="element-433" data-mp="1zdf2mktgryx5">
              We couldn't load the consent details.
            </p>
            <button type="button" onClick={() => {
            searchFired.current = true;
            runTcpaSearch();
          }} className="mt-2 inline-flex items-center rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-100 transition-colors duration-200" data-id="element-434" data-mp="2s2cjcji11nns">
              Retry
            </button>
          </div> : <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-4 w-4 rounded border-gray-300 cursor-pointer flex-shrink-0 accent-[#1668E0]" data-id="element-435" data-mp="u5chvk9l7wek">
            {tcpa?.disclosure ? <span dangerouslySetInnerHTML={{
            __html: sanitizeTcpaHtml(tcpa.disclosure)
          }} data-id="element-436" data-mp="3tnvniev0jkr8" /> : 'By submitting this form, you are giving your express written consent to be contacted regarding educational opportunities by email, telephone, or text, including through the use of automated technology, at the number provided above. Consent is not a condition of enrollment.'}
          </TCPADisclosure>}
        </div>

        {submitError && <p className="text-xs text-red-600" data-id="element-437" data-mp="2i6asoct5xyzk">{submitError}</p>}

        <div className={`flex gap-3 pt-1 ${wide ? 'sm:max-w-md sm:ml-auto' : ''}`} data-id="element-438" data-mp="2n4cgfb1jamhd">
          <button type="button" onClick={() => setStep(2)} disabled={isSubmitting} className={backButton} data-id="element-439" data-mp="2s35tj2cwsbh0">
            <ChevronLeftIcon className="w-4 h-4 mr-1" aria-hidden="true" data-id="element-440" data-mp="2w2y4tmtosvq2" />
            Back
          </button>
          <button type="submit" disabled={isSubmitting} className={primaryButton} data-id="element-441" data-mp="311r68gvp8w9v">
            {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" aria-hidden="true" data-id="element-442" data-mp="3aslwrxfzai14" />}
            {isSubmitting ? 'Finding School Matches...' : 'See My Matches'}
          </button>
        </div>
      </form>;
  };
  return <div className="bg-white rounded-2xl shadow-xl overflow-hidden ring-1 ring-gray-900/5" data-id="element-443" data-mp="3js8x0fwz1hsl">
      <div className="bg-navy px-6 py-4" style={{
      backgroundColor: "#8FBEF5"
    }} data-id="element-444" data-mp="1855f56cc2r1u">
        <h2 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-445" data-mp="1443fioei8czc">
          {heading}
        </h2>
        <p className="text-white/70 text-xs text-center mt-1" data-id="element-446" data-mp="z8ek8x9ipi8u">{subtext}</p>
      </div>

      <div className={wide ? 'px-6 sm:px-10 pt-5' : 'px-6 pt-5'} data-id="element-447" data-mp="u9l7j7221v9q">
        <div className="flex items-center justify-between mb-1.5" data-id="element-448" data-mp="1rtxo2trsjfds">
          <span className="text-xs font-bold uppercase tracking-wider text-navy" data-id="element-449" data-mp="1mwe7hn7meqio">
            Step {currentStepNumber} of {totalSteps} · {STEP_LABELS[step]}
          </span>
          <span className="text-xs text-gray-500" data-id="element-450" data-mp="1i332lrmdcjte">{progressPercent}%</span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden" role="progressbar" aria-valuenow={progressPercent} aria-valuemin={0} aria-valuemax={100} aria-label="Form progress" data-id="element-451" data-mp="1d48lqv7yp5bx">
          <motion.div className="h-full rounded-full bg-crimson" initial={false} animate={{
          width: `${progressPercent}%`
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-452" data-mp="3yrh992s3cn3" />
        </div>
      </div>

      <div className={wide ? 'p-6 sm:p-10' : 'p-6'} data-id="element-453" data-mp="3vbvtiyjj8zeu">
        <AnimatePresence mode="wait" initial={false} data-id="element-454" data-mp="3vab2vk0ca0m9">
          {isSubmitting ?
        // Takes over the card: there is nothing left to answer, and
        // leaving editable fields on screen would invite edits that can
        // no longer reach the request.
        <motion.div key="finding" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.18,
          ease: [0.23, 1, 0.32, 1]
        }} className="py-10 text-center" role="status" aria-live="polite" data-id="element-455" data-mp="37fi2uwaufy9w">
              <LoaderIcon className="w-8 h-8 mx-auto mb-4 text-navy animate-spin" aria-hidden="true" data-id="element-456" data-mp="1kpll1euxygac" />
              <p className="text-base font-bold text-navy" data-id="element-457" data-mp="1fsbn1athkwaw">
                Finding School Matches
              </p>
              <p className="mt-1.5 text-sm text-gray-500" data-id="element-458" data-mp="1tk9qb50inxz5">
                Checking accredited schools against your answers.
              </p>
            </motion.div> : <motion.div key={step} initial={{
          opacity: 0,
          y: reduceMotion ? 0 : 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: reduceMotion ? 0 : -8
        }} transition={{
          duration: 0.18,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-459" data-mp="23erxik556bnp">
              {renderStep()}
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}